<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { useComparisonConfigStore } from "@/config/config-store";

import { getCategoryForObject } from "../comparison-utils";

import type { ComparisonObject } from "../comparison-data-service";

const props = defineProps<{
  columnLayout: {
    gridTemplateColumns: string;
    minWidth: string;
    stickyWidth: number;
  };
  typeBoundaryIndex: number;
  expanded: boolean;
  expandable: boolean;
  categoryName: string;
  objects: ComparisonObject[];
  subjectObject: ComparisonObject | undefined;
}>();

const configStore = useComparisonConfigStore();
const { colors, fontSize } = storeToRefs(configStore);

function correctionCellStyle(index: number) {
  if (index !== 0) return undefined;
  return {
    left: `${props.columnLayout.stickyWidth}px`,
    backgroundColor: colors.value.accordionHeaderBackground,
  };
}

const subjectCategory = computed(() => {
  if (!props.subjectObject) return undefined;
  return getCategoryForObject(props.subjectObject, props.categoryName);
});

const corrections = computed(() => {
  return props.objects.map((obj) => {
    if (obj === props.subjectObject) {
      return { name: obj.name.value, text: "", isSubject: true };
    }

    const category = getCategoryForObject(obj, props.categoryName);
    if (!category || !subjectCategory.value)
      return { name: obj.name.value, text: "", isSubject: false };
    const diff =
      (category.Correction / subjectCategory.value.Correction - 1) * 100;
    const sign = diff >= 0 ? "+" : "";
    const formatted = diff.toLocaleString("nl-NL", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return {
      name: obj.name.value,
      text: `${sign}${formatted}%`,
      isSubject: false,
      color: diff > 0 ? "green" : diff < 0 ? "red" : "gray",
    };
  });
});
</script>

<template>
  <div
    class="grid w-full"
    :style="{ ...columnLayout, fontSize: `${fontSize}px` }"
  >
    <div
      class="sticky left-0 z-10 flex items-center gap-2 px-4 py-3"
      :style="{ backgroundColor: colors.accordionHeaderBackground }"
    >
      <i
        v-if="expandable"
        class="pi text-xs text-gray-500"
        :class="expanded ? 'pi-chevron-down' : 'pi-chevron-right'"
      />
      <span class="font-semibold text-gray-900">
        {{ categoryName }}
      </span>
    </div>
    <div
      v-for="(correction, index) in corrections"
      :key="correction.name"
      class="px-4 py-3 text-center font-medium text-gray-900"
      :class="[
        index === 0 ? 'sticky z-10' : '',
        index === typeBoundaryIndex ? 'border-l-2 border-gray-300' : '',
      ]"
      :style="correctionCellStyle(index)"
    >
      <span v-if="correction.isSubject" class="text-gray-400">–</span>
      <span
        v-else
        class="inline-block rounded-full px-2.5 py-0.5 font-semibold"
        :class="{
          'bg-green-50 text-green-700': correction.color === 'green',
          'bg-red-50 text-red-600': correction.color === 'red',
          'text-gray-500': correction.color === 'gray',
        }"
      >
        {{ correction.text }}
      </span>
    </div>
  </div>
</template>
